import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js"

class ShippingRates extends HTMLElement {
    constructor() {
        super();

        this.elements = {
            defaultMessage: this.querySelector('[data-default-message]'),
            button: this.querySelector('[data-toggle-form]'),
            form: this.querySelector('[data-form]'),
            message: this.querySelector('[data-message]'),
            ratesList: this.querySelector('[data-shipping-rates]'),
            zipInput: this.querySelector('[name="zip"]'),
            buttonCheckout: document.querySelectorAll("[data-submit]")
        };

        this.init();
    }

    init() {
        // Validar elementos essenciais
        if (!this.elements.button || !this.elements.form || !this.elements.zipInput) {
            console.error('ShippingRates: Elementos essenciais não encontrados');
            return;
        }

        this.addEventListeners();
        if (this.elements.ratesList) this.hideElement(this.elements.ratesList);
        if (this.elements.form) this.hideElement(this.elements.form);
        this.applyCepMask();
        this.disableCheckoutButtons();
    }

    addEventListeners() {
        if (this.elements.button) {
            this.elements.button.addEventListener('click', () => this.toggleForm());
        }
        if (this.elements.form) {
            this.elements.form.addEventListener('submit', (event) => this.handleSubmit(event));
        }
        if (this.elements.zipInput) {
            this.elements.zipInput.addEventListener('input', () => this.formatCep());
        }
    }

    applyCepMask() {
        if (this.elements.zipInput) {
            this.elements.zipInput.setAttribute('maxlength', '9');
        }
    }

    formatCep() {
        if (!this.elements.zipInput) return;
        
        let cep = this.elements.zipInput.value.replace(/\D/g, '');
        if (cep.length > 5) {
            cep = cep.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
        }
        this.elements.zipInput.value = cep;
    }

    toggleForm() {
        if (this.elements.form) this.showElement(this.elements.form);
        if (this.elements.ratesList) this.hideElement(this.elements.ratesList);
        this.clearMessage();

        if (this.elements.button && this.elements.button.hasAttribute('shipping-rates')) {
            this.elements.button.textContent = 'Consultar';
            this.elements.button.removeAttribute('shipping-rates');
        }
    }

 clearMessage() {
  const root = document.querySelector("#agendamento");
  const cutoffHour = Number(root?.dataset?.cutoffHour ?? 17);

  if (this.elements.defaultMessage) {
    this.elements.defaultMessage.textContent = `Compre de segunda a sábado até às ${cutoffHour}h e receba no mesmo dia. Exclusivo para entregas para Grande São Paulo.`;
  }
  if (this.elements.message) {
    this.elements.message.textContent = '';
    this.hideElement(this.elements.message);
  }
  this.disableCheckoutButtons();
}


    showElement(element) {
        if (element) {
            element.setAttribute('show', 'true');
        }
    }

    hideElement(element) {
        if (element) {
            element.removeAttribute('show');
        }
    }

    disableCheckoutButtons() {
        this.elements.buttonCheckout.forEach(button => button.setAttribute('disabled', 'true'));

        document.querySelectorAll("[data-submit]").forEach(button => {
            button.setAttribute("disabled", "true");
            button.setAttribute("data-disabled", "true");
        });
    }

    enableCheckoutButtons() {
        this.elements.buttonCheckout.forEach(button => button.removeAttribute('disabled'));
        
        document.querySelectorAll("[data-submit]").forEach(button => {
            button.removeAttribute("disabled");
            button.removeAttribute("data-disabled");
        });
    }

    async handleSubmit(event) {
        event.preventDefault();
        this.clearMessage();
        this.hideElement(this.elements.ratesList);

        const buttonSubmit = event.currentTarget.querySelector('button[type="submit"]');
        if (buttonSubmit) {
            buttonSubmit.setAttribute('disabled', 'true');
            buttonSubmit.textContent = 'Consultando...';
        }

        const zip = this.elements.zipInput.value.replace(/\D/g, '');

        try {
            const { state, cep: zipCode, city } = await this.fetchCepData(zip);
            
            // Validação adicional: garantir que temos todos os dados necessários
            if (!state || !zipCode || !city) {
                throw new Error('Dados do CEP incompletos. Por favor, verifique o CEP e tente novamente.');
            }
            
            const shippingRates = await this.fetchShippingRates(state, zipCode, city);
            this.displayShippingRates(shippingRates);
            this.updateButton(zip);
        } catch (error) {
            // Verificar se é erro técnico (422, timeout, etc.) - permitir continuar
            // Mas NÃO tratar erros de "CEP fora da área" como técnicos
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isCepForaArea = errorMessage.includes('CEP fora da área de entrega');
            
            if (error.isTechnicalError && !isCepForaArea) {
                console.error("Erro técnico ao buscar taxas de envio (permitindo continuar):", {
                    message: error.message,
                    statusCode: error.statusCode,
                    userMessage: error.userMessage,
                    originalMessage: error.originalMessage
                });
                
                // Usar mensagem específica se disponível, senão mensagem genérica
                const technicalMessage = error.userMessage || error.message || 'Erro técnico ao calcular frete';
                console.warn(technicalMessage);
                
                // Mostrar agendamento e liberar checkout mesmo com erro técnico
                this.handleTechnicalError(zip, buttonSubmit);
                return;
            }
            
            // Para outros erros (incluindo CEP fora da área), mostrar erro normalmente
            let finalErrorMessage = errorMessage;
            
            // Usar mensagem específica se disponível
            if (error.userMessage) {
                finalErrorMessage = error.userMessage;
            }
            
            this.displayError(finalErrorMessage);
        }

        if (buttonSubmit) {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.textContent = 'Consultar';
        }
    }

    async fetchCepData(zip) {
        const RETRIES = 3;
        const RETRY_DELAY_MS = 500;

        const fetchWithTimeout = async (url, timeout = 2000) => {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), timeout);
            try {
                const res = await fetch(url, { signal: controller.signal });
                clearTimeout(id);
                if (!res.ok) throw new Error('Network response was not ok');
                const text = await res.text();
                if (!text || text.trim().length === 0) throw new Error('Resposta vazia da API de CEP');
                try {
                    return JSON.parse(text);
                } catch (parseError) {
                    throw new Error('Resposta inválida da API de CEP');
                }
            } catch (err) {
                clearTimeout(id);
                throw err;
            }
        };

        const fetchWithSilentRetries = async (url) => {
            let lastErr;
            for (let r = 0; r < RETRIES; r++) {
                if (r > 0) await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
                try {
                    return await fetchWithTimeout(url, 2000);
                } catch (e) {
                    lastErr = e;
                }
            }
            throw lastErr;
        };

        const brasilapiUrl = `https://brasilapi.com.br/api/cep/v1/${zip}`;
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const data = await fetchWithSilentRetries(brasilapiUrl);
                if (data && (data.state || data.uf)) {
                    const city = data.city || data.city_name || '';
                    if (!city) break;
                    return { state: data.state || data.uf, cep: data.cep || zip, city };
                }
            } catch (e) {
                // continua para próxima tentativa ou ViaCEP
            }
        }

        try {
            const via = await fetchWithSilentRetries(`https://viacep.com.br/ws/${zip}/json/`);
            if (via && !via.erro) {
                const city = via.localidade || '';
                if (!city) throw new Error('CEP encontrado mas cidade não disponível. Por favor, verifique o CEP.');
                return { state: via.uf, cep: via.cep || zip, city };
            }
            throw new Error('CEP não encontrado');
        } catch (e) {
            if (e.message && e.message.includes('cidade não disponível')) throw e;
            throw new Error('CEP não encontrado, por favor, verifique se o CEP está correto e tente novamente.');
        }
    }

    async fetchShippingRates(state, zip, city) {
        // Nota: O Shopify não permite atualizar o shipping_address diretamente via /cart/update.js
        // O endereço deve ser enviado apenas no prepare_shipping_rates
        
        const cartFetchWithRetries = async () => {
            const maxRetries = 3;
            const retryDelayMs = 500;
            let lastErr;
            for (let r = 0; r < maxRetries; r++) {
                if (r > 0) await this.sleep(retryDelayMs);
                try {
                    const res = await fetch('/cart.js');
                    if (!res.ok) throw new Error('Não foi possível verificar o carrinho');
                    const text = await res.text();
                    if (!text || text.trim().length === 0) throw new Error('Resposta vazia');
                    const cart = JSON.parse(text);
                    if (!cart.items || cart.items.length === 0) throw new Error('O carrinho está vazio. Adicione itens antes de calcular o frete.');
                    return cart;
                } catch (e) {
                    lastErr = e;
                    if (e.message && e.message.includes('vazio')) throw e;
                }
            }
            throw lastErr;
        };
        try {
            const cart = await cartFetchWithRetries();
            console.log(`Carrinho validado: ${cart.items.length} item(ns)`);
        } catch (cartError) {
            if (cartError.message && cartError.message.includes('vazio')) throw cartError;
            console.warn('Aviso: Não foi possível validar carrinho, continuando mesmo assim...');
        }
        
        // Normalizar dados do endereço
        const cleanZip = zip.replace(/\D/g, ''); // Remove tudo que não é número
        // Formatar CEP com hífen (formato brasileiro: 70040-010)
        const formattedZip = cleanZip.length === 8 
            ? `${cleanZip.substring(0, 5)}-${cleanZip.substring(5)}` 
            : cleanZip;
        
        // Normalizar cidade: remover acentos e capitalizar
        const normalizeCity = (str) => {
            return str
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '') // Remove acentos
                .trim();
        };
        const normalizedCity = normalizeCity(city);
        
        // Validar formato do estado (deve ser código de 2 letras)
        const normalizedState = state && state.length === 2 
            ? state.toUpperCase() 
            : state;
        
        // Criar payload com formato otimizado para Shopify
        const shippingAddress = {
            country: 'BR',
            province: normalizedState,
            zip: formattedZip, // CEP formatado com hífen
            city: normalizedCity
        };
        
        const payload = JSON.stringify({ shipping_address: shippingAddress });
        
        // Log para debug com CEP consultado
        console.log('📤 Payload enviado para prepare_shipping_rates:', {
            ...shippingAddress,
            cepConsultado: zip,
            timestamp: new Date().toISOString()
        });

        try {
            // Primeira requisição para preparar as taxas
            // CRÍTICO: Validar se prepare_shipping_rates foi bem-sucedido antes de continuar
            let prepareSuccess = false;
            try {
                const prepareResponse = await this.requestShippingRates('/cart/prepare_shipping_rates', payload, null, true);
                prepareSuccess = true;
                console.log('prepare_shipping_rates: Sucesso', prepareResponse);
            } catch (prepareError) {
                // Log detalhado do erro para diagnóstico
                console.error('prepare_shipping_rates: Erro capturado', {
                    message: prepareError.message,
                    statusCode: prepareError.statusCode,
                    isTechnicalError: prepareError.isTechnicalError,
                    originalMessage: prepareError.originalMessage,
                    error: prepareError
                });
                
                // Se prepare falhar com "resposta vazia", pode ser normal (Shopify pode não retornar nada)
                if (prepareError.message && prepareError.message.includes('Resposta vazia')) {
                    console.log('prepare_shipping_rates: Resposta vazia (normal, continuando...)');
                    prepareSuccess = true; // Resposta vazia é aceitável
                } 
                // Se for erro 422 no prepare, NÃO continuar - isso é a causa raiz do problema
                else if (prepareError.isTechnicalError && prepareError.statusCode === 422) {
                    console.error('prepare_shipping_rates: Erro 422 - Payload inválido. Detalhes:', prepareError.originalMessage);
                    throw new Error(`Erro ao preparar cálculo de frete: ${prepareError.originalMessage || 'Dados do endereço inválidos. Verifique o CEP e tente novamente.'}`);
                }
                // Para outros erros, relançar
                else {
                    throw prepareError;
                }
            }
            
            // Se prepare_shipping_rates falhou, não continuar
            if (!prepareSuccess) {
                throw new Error('Não foi possível preparar o cálculo de frete. Tente novamente.');
            }


            // shipping_rates.json: retentativas silenciosas só quando a requisição falha (resposta vazia/rede). Resposta válida com shipping_rates: [] = CEP não entrega (validação preservada).
            const fetchRatesOnce = async () => {
                const response = await fetch('/cart/shipping_rates.json', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin',
                    body: payload
                });
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Erro desconhecido');
                    throw new Error(`Erro ao consultar frete: ${response.status} - ${errorText}`);
                }
                const text = await response.text();
                if (!text || text.trim().length === 0) throw new Error('Resposta vazia do servidor');
                try {
                    return JSON.parse(text);
                } catch (e) {
                    throw new Error('Resposta inválida do servidor');
                }
            };

            const maxRetries = 3;
            const retryDelayMs = 500;
            let shippingRates;
            let lastErr;
            for (let r = 0; r < maxRetries; r++) {
                if (r > 0) await this.sleep(retryDelayMs);
                try {
                    shippingRates = await fetchRatesOnce();
                    break;
                } catch (e) {
                    lastErr = e;
                }
            }
            if (typeof shippingRates === 'undefined') throw lastErr;
            if (shippingRates && shippingRates.shipping_rates && shippingRates.shipping_rates.length > 0) {
                console.log(`✅ Taxas encontradas:`, shippingRates.shipping_rates.length, 'taxas');
            }
            return shippingRates;
        } catch (error) {
            console.error("Erro ao buscar taxas de envio:", error);
            // Se já é um Error com mensagem, relançar
            if (error instanceof Error && error.message) {
                throw error;
            }
            // Caso contrário, erro genérico
            throw new Error('Não foi possível obter as taxas de envio. Tente novamente.');
        }
    }
 
    async requestShippingRates(url, body = null, signal = null, allowEmpty = false) {
        const RETRIES = 3;
        const RETRY_DELAY_MS = 500;
        let lastErr;
        for (let attempt = 0; attempt < RETRIES; attempt++) {
            if (attempt > 0) await this.sleep(RETRY_DELAY_MS);
            try {
                const headers = {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                };
                if (body) headers['Content-Type'] = 'application/json';
                const options = {
                    method: body ? 'POST' : 'GET',
                    headers,
                    signal: signal || undefined,
                    credentials: 'same-origin'
                };
                if (body) options.body = body;
                const response = await fetch(url, options);
                if (signal?.aborted) throw new DOMException('A requisição foi abortada', 'AbortError');
                if (!response.ok) {
                    let errorText = 'Erro desconhecido';
                    let errorData = null;
                    try {
                        errorText = await response.text();
                        try { errorData = JSON.parse(errorText); } catch (e) {}
                    } catch (e) {}
                    if (response.status === 422) {
                        const error = new Error('Erro ao calcular frete: dados do endereço inválidos ou incompatíveis');
                        error.isTechnicalError = true;
                        error.statusCode = 422;
                        error.originalMessage = errorText;
                        error.errorData = errorData;
                        if (errorData && errorData.error && Array.isArray(errorData.error)) error.userMessage = errorData.error.join('. ');
                        else if (errorData && errorData.error) error.userMessage = String(errorData.error);
                        else error.userMessage = 'Não foi possível calcular o frete. Verifique o CEP e tente novamente.';
                        throw error;
                    }
                    const httpError = new Error(`Erro ao consultar frete (${response.status}): ${errorText.substring(0, 200)}`);
                    httpError.statusCode = response.status;
                    httpError.originalMessage = errorText;
                    httpError.errorData = errorData;
                    throw httpError;
                }
                const contentType = response.headers.get('content-type') || '';
                const text = await response.text();
                if (!text || text.trim().length === 0) {
                    if (allowEmpty) return null;
                    if (url.includes('prepare_shipping_rates')) return { status: 'preparing' };
                    throw new Error('Resposta vazia do servidor. Tente novamente.');
                }
                if (!contentType.includes('application/json') && !contentType.includes('text/json') && text.trim().startsWith('<')) {
                    throw new Error('O servidor retornou uma página de erro. Tente novamente.');
                }
                try {
                    return JSON.parse(text);
                } catch (parseError) {
                    throw new Error('Resposta inválida do servidor. Tente novamente.');
                }
            } catch (error) {
                if (error.name === 'AbortError' || error instanceof DOMException) throw error;
                if (error.isTechnicalError && error.statusCode === 422) throw error;
                lastErr = error;
            }
        }
        if (lastErr instanceof Error) throw lastErr;
        throw new Error('Erro de rede ao consultar frete');
    }

    displayShippingRates(shippingRates) {
        // Validação CEP: resposta válida com shipping_rates vazio = CEP fora da área (não retentativa)
        // LOG CRÍTICO: Ver taxas ANTES do filtro para debug
        console.log('📦 Taxas recebidas do Shopify (ANTES do filtro):', {
            total: shippingRates?.shipping_rates?.length || 0,
            timestamp: new Date().toISOString(),
            taxas: shippingRates?.shipping_rates?.map(r => ({
                name: r.name,
                source: r.source,
                price: r.price
            })) || []
        });
        
        // Validação: Se shippingRates é null ou inválido (não é o caso emptyAfterRetries), lançar erro
        if (!shippingRates || !shippingRates.shipping_rates) {
            console.error('❌ shippingRates é null ou inválido:', shippingRates);
            throw new Error('Ops, CEP fora da área de entrega. Se quiser fazer a retirada, utilize o CEP 04549-002');
        }
        
        // Guardar quantidade antes do filtro para validação
        const taxasAntesFiltro = shippingRates?.shipping_rates?.length || 0;
        
        // remover taxa de backup se existir (FILTRAR PRIMEIRO, como no código funcional)
        shippingRates.shipping_rates = shippingRates.shipping_rates
            .filter(rate => !rate.source.toLowerCase().includes('shopify_provided_generic_layer'))
            .filter(rate => !rate.source.toLowerCase().includes('lalamove_carrier_service'));

        // LOG CRÍTICO: Ver taxas DEPOIS do filtro para debug
        console.log('📦 Taxas após filtro:', {
            total: shippingRates?.shipping_rates?.length || 0,
            removidas: taxasAntesFiltro - (shippingRates?.shipping_rates?.length || 0),
            taxas: shippingRates?.shipping_rates?.map(r => ({
                name: r.name,
                source: r.source,
                price: r.price
            })) || []
        });

        // Validar APÓS filtrar (como no código funcional)
        if (!shippingRates?.shipping_rates?.length) {
            // Se havia taxas antes do filtro mas não depois, pode ser problema do filtro
            if (taxasAntesFiltro > 0) {
                console.warn('⚠️ ATENÇÃO: Havia taxas antes do filtro, mas todas foram removidas pelo filtro. Pode ser filtro muito agressivo.');
            }
            throw new Error('Ops, CEP fora da área de entrega. Se quiser fazer a retirada, utilize o CEP 04549-002');
        }

        // verificar se opção de entrega tem opção de retirada ou Entrega Recomendada - Agendada
        // Busca flexível com múltiplas variações para evitar problemas com nomes diferentes
        const hasRetirada = shippingRates.shipping_rates.some(rate => {
            const nameLower = rate.name.toLowerCase();
            return nameLower.includes('retirada') || 
                   nameLower.includes('retirar') || 
                   nameLower.includes('pickup') ||
                   nameLower.includes('pick-up');
        });
        
        const hasEntregaRecomendada = shippingRates.shipping_rates.some(rate => {
            const nameLower = rate.name.toLowerCase();
            return nameLower.includes('entrega recomendada - agendada') ||
                   nameLower.includes('entrega agendada') ||
                   nameLower.includes('agendada') ||
                   nameLower.includes('agendamento');
        });
        
        const localDelivery = shippingRates.shipping_rates.some((e) => {
            const nameLower = e.name.toLowerCase();
            return nameLower.includes("local delivery") ||
                   nameLower.includes("entrega local");
        });
        
        // LOG: Mostrar quais taxas foram detectadas
        console.log('🔍 Verificação de opções de entrega:', {
            hasRetirada,
            hasEntregaRecomendada,
            localDelivery,
            todasTaxas: shippingRates.shipping_rates.map(r => r.name)
        });

        if (!hasRetirada && !hasEntregaRecomendada && !localDelivery) {
            console.log("Não há opção de retirada, entrega recomendada ou local delivery disponível.");

            // adicionar mensagem informando que não há opção de retirada
            this.elements.defaultMessage.textContent = "Oba! Entregamos no CEP indicado. Mas se quiser retirar diretamente na loja na Vila Olímpia (SP), utilize o CEP 04549-002 e agende o melhor dia para retirada.";

            // liberar o checkout
            this.enableCheckoutButtons();

            // Retornar sem mostrar agendamento (como no código funcional)
            return;
        }

        // Adicione este código para exibir o frete
        const freteValorElement = document.getElementById('frete-valor');
        if (freteValorElement && shippingRates.shipping_rates.length > 0) {
            const rate = shippingRates.shipping_rates[0];

            // Log para debug (você pode remover depois)
            console.log("Frete original:", rate.price, typeof rate.price);

            // Garante que o valor é um número
            let valorFrete = 0;

            try {
                // Tenta converter para número, independente do formato
                if (typeof rate.price === 'string') {
                    valorFrete = Number(rate.price.replace(/[^\d.,]/g, '').replace(',', '.'));
                } else {
                    valorFrete = Number(rate.price);
                }

                // Se o valor for muito alto, provavelmente está em centavos
                if (valorFrete > 100) {
                    valorFrete = valorFrete / 100;
                }

                // Se for grátis
                if (valorFrete === 0) {
                    freteValorElement.textContent = "Frete: Grátis";
                } else {
                    freteValorElement.textContent = `Frete: R$ ${valorFrete.toFixed(2).replace('.', ',')}`;
                }
            } catch (error) {
                console.error("Erro ao processar valor do frete:", error);
                freteValorElement.textContent = "Frete: Consultar";
            }
        }

        // Só mostrar agendamento se chegou até aqui (tem retirada/entrega recomendada)
        console.log('Mostrando agendamento - CEP tem retirada/entrega recomendada');
        this.showElement(this.elements.ratesList);
    }

    updateButton(zip) {
        if (this.elements.button) {
            this.elements.button.textContent = zip;
            this.elements.button.setAttribute('shipping-rates', 'true');
        }
        if (this.elements.form) {
            this.hideElement(this.elements.form);
        }
    }

    handleTechnicalError(zip, buttonSubmit = null) {
        // Em caso de erro técnico (422, timeout, etc.), permitir que o usuário continue
        // Mostrar agendamento e liberar checkout, mas não mostrar mensagem de erro
        
        // Resetar botão de submit do formulário
        if (buttonSubmit) {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.textContent = 'Consultar';
        }
        
        // Atualizar botão com o CEP
        this.updateButton(zip);
        
        // Mostrar o agendamento (ratesList)
        if (this.elements.ratesList) {
            this.showElement(this.elements.ratesList);
        }
        
        // Limpar mensagens de erro
        if (this.elements.message) {
            this.elements.message.textContent = '';
            this.hideElement(this.elements.message);
        }
        
        // Atualizar mensagem padrão para informar que pode continuar
        if (this.elements.defaultMessage) {
            const root = document.querySelector("#agendamento");
            const cutoffHour = Number(root?.dataset?.cutoffHour ?? 17);
            this.elements.defaultMessage.textContent = `Compre de segunda a sábado até às ${cutoffHour}h e receba no mesmo dia. Exclusivo para entregas para Grande São Paulo.`;
        }
        
        // Liberar botões de checkout
        this.enableCheckoutButtons();
        
        // Log no console apenas (não mostrar para o usuário)
        console.warn('Erro técnico ao calcular frete, mas permitindo continuar com o agendamento.');
    }

    displayError(message) {
        // Garantir que temos um elemento de mensagem válido
        if (!this.elements.message) {
            console.error('Elemento de mensagem não encontrado:', message);
            return;
        }
        
        // Garantir que message é uma string
        const errorMessage = typeof message === 'string' ? message : 'Ocorreu um erro. Por favor, tente novamente.';
        
        console.log('Exibindo erro e bloqueando checkout/agendamento:', errorMessage);
        
        // Mostrar mensagem de erro
        this.elements.message.textContent = errorMessage;
        this.showElement(this.elements.message);
        
        // CRÍTICO: Bloquear checkout e agendamento quando há erro
        this.disableCheckoutButtons();
        this.hideElement(this.elements.ratesList);
        
        // Garantir que o formulário de agendamento também está escondido
        if (this.elements.form) {
            this.hideElement(this.elements.form);
        }
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

customElements.define('shipping-rates', ShippingRates);


class DataSelect extends HTMLElement {
    constructor() {
        super();
        this.today = new Date();
        this.maxDate = new Date();
        this.diasUteisAdicionados = 0;
        this.daysAvailable = 20;
        this.feriadosNacionais = [];
        this.feriadosVariaveis = [];
        this.feriados = [];
        this.init();
    }

    async init() {
        this.daysAvailable = this.getAttribute("data-days-available") ? parseInt(this.getAttribute("data-days-available")) : 20;

        this.getFeriados();
        this.calcularPeriodoDisponivel();
        this.configurarFlatpickr();
    }

    getFeriados() {
        const feriados = this.getAttribute("data-feriados")

        if(feriados) {
            const feriadosFromArray = feriados.split(",")

            this.feriados = feriadosFromArray
        }
    }

    calcularPeriodoDisponivel() {
  const root = document.querySelector("#agendamento");
  const cutoffHour = Number(root?.dataset?.cutoffHour ?? 17);

  let brtNow = new Date(this.today.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  let minDate = brtNow.getHours() >= cutoffHour ? this.getNextBusinessDay(new Date(brtNow)) : brtNow;

        while (this.diasUteisAdicionados < this.daysAvailable) {
            this.maxDate.setDate(this.maxDate.getDate() + 1);
            if (this.isDiaUtil(this.maxDate)) {
                this.diasUteisAdicionados++;
            }
        }

        this.minDate = minDate;
    }

    configurarFlatpickr() {
        flatpickr("#data", {
            locale: Portuguese,
            minDate: this.minDate, 
            maxDate: this.maxDate,
            dateFormat: "d/m/Y",
            disable: [(date) => !this.isDiaUtil(date)],
            onChange: (selectedDates, dateStr, instance) => this.handleDateChange(selectedDates, dateStr, instance)
        });
    }

    isDiaUtil(date) {
        return date.getDay() !== 0 && !this.feriados.includes(this.formatDate(date));
    }

    normalizeDate(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    isDateSelectable(date) {
        const selected = this.normalizeDate(date);
        const min = this.normalizeDate(this.minDate);
        const max = this.normalizeDate(this.maxDate);

        return selected >= min && selected <= max && this.isDiaUtil(selected);
    }

    handleDateChange(selectedDates, dateStr, instance) {
        const toggleButtons = (shouldDisable) => {
            document.querySelectorAll("[data-submit]").forEach(button => {
                if (shouldDisable) {
                    button.setAttribute("disabled", "true");
                    button.setAttribute("data-disabled", "true");
                } else {
                    button.removeAttribute("disabled");
                    button.removeAttribute("data-disabled");
                }
            });
        };

        const selectedDate = selectedDates[0];

        if (!selectedDate || !this.isDateSelectable(selectedDate)) {
            // Impede uso de datas bloqueadas mesmo que o DOM seja manipulado
            toggleButtons(true);
            if (instance) instance.clear();
            console.warn('Data selecionada fora do intervalo permitido ou em dia indisponível.');
            return;
        }

        document.querySelectorAll('[name="attributes[delivery schedule date]"]').forEach(input => input.value = dateStr);
        document.querySelectorAll('[name="note"]').forEach(input => input.value = dateStr);

        toggleButtons(true);

        console.log('Atualizando carrinho com a data selecionada:', dateStr);

        fetch('/cart/update', {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify({ note: dateStr })
        })
        .then(response => {
            if (response.ok) {
                toggleButtons(false);
                return;
            }
            throw new Error('Falha ao atualizar o carrinho com a data selecionada.');
        })
        .catch(error => {
            console.error('Erro ao atualizar carrinho:', error);
            toggleButtons(false);
        });
    }

    calcularFeriadosVariaveis(ano) {
        let a = ano % 19;
        let b = Math.floor(ano / 100);
        let c = ano % 100;
        let d = Math.floor(b / 4);
        let e = b % 4;
        let f = Math.floor((b + 8) / 25);
        let g = Math.floor((b - f + 1) / 3);
        let h = (19 * a + b - d - g + 15) % 30;
        let i = Math.floor(c / 4);
        let k = c % 4;
        let l = (32 + 2 * e + 2 * i - h - k) % 7;
        let m = Math.floor((a + 11 * h + 22 * l) / 451);
        let mes = Math.floor((h + l - 7 * m + 114) / 31);
        let dia = ((h + l - 7 * m + 114) % 31) + 1;

        let pascoa = new Date(ano, mes - 1, dia);
        return [
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() - 47))), // Carnaval
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() + 45))), // Sexta-feira Santa
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() + 60)))  // Corpus Christi
        ];
    }

    formatDate(date) {
        return date.toISOString().split('T')[0];
    }

    getNextBusinessDay(date) {
        do {
            date.setDate(date.getDate() + 1);
        } while (!this.isDiaUtil(date));
        return date;
    }
}

customElements.define('data-select', DataSelect);

class ButtonRemoveKit extends HTMLElement {
    constructor() {
        super()

        this.hasMinicart = this.hasAttribute("data-minicart")
        this.init()
    }

    init() {
        this.addEventListener("click", (event) => this.handleRemove(event))
    }

    async  handleRemove(event) {
        event.preventDefault();
        const { currentTarget } = event

        try {
            const montagemGroup = currentTarget.getAttribute('data-montagem-group'); // Pega o grupo de montagem do pai
            if (!this.hasMinicart) currentTarget.innerHTML = '<span>Removendo...</span>';

            if (this.hasMinicart) {
                const spinner = this.querySelector('.loading__spinner ');
                spinner.classList.remove('hidden');

                const spanText = this.querySelector('span:not(.loading__spinner )');
                if (spanText) spanText.classList.add('hidden');
            }

            currentTarget.style.pointerEvents = 'none'; // Desabilita o botão durante o processo

            const response = await fetch('/cart.js');
            
            if (!response.ok) {
                throw new Error(`Erro ao buscar carrinho: ${response.status}`);
            }
            
            // Verificar se a resposta tem conteúdo antes de fazer parse
            const text = await response.text();
            if (!text || text.trim().length === 0) {
                throw new Error('Resposta vazia do servidor');
            }
            
            let body;
            try {
                body = JSON.parse(text);
            } catch (parseError) {
                console.error('Erro ao fazer parse do JSON do carrinho:', parseError);
                throw new Error('Resposta inválida do servidor');
            }

            if(body.items) {
                await this.removeItemMotagem(body.items, montagemGroup)
            }
        } catch (error) {
            console.log(`Erro handleRemove: ${error}`)
        } finally {
            if (!this.hasMinicart) currentTarget.innerHTML = '<span>Remover</span>';

            currentTarget.style.pointerEvents = 'all';
        }
    }

    async removeItemMotagem(items, montagemGroup) {
        try {
            const updates = Object.fromEntries(
                items
                    .filter((item) => item.properties && item.properties.Montagem === montagemGroup)
                    .map(item => [item.key, 0])
            );

            fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ updates })
            })
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error(`Erro ao atualizar carrinho: ${response.status}`);
                    }
                    
                    // Verificar se a resposta tem conteúdo antes de fazer parse
                    const text = await response.text();
                    if (!text || text.trim().length === 0) {
                        throw new Error('Resposta vazia do servidor');
                    }
                    
                    try {
                        return JSON.parse(text);
                    } catch (parseError) {
                        console.error('Erro ao fazer parse do JSON do carrinho:', parseError);
                        throw new Error('Resposta inválida do servidor');
                    }
                })
                .then(async (response) => {
                    if (this.hasMinicart && window.location.pathname !== '/cart') {
                        const response = await fetch('/cart?section_id=cart-drawer');
                        const responseText = await response.text();

                        const html = new DOMParser().parseFromString(responseText, "text/html");
                        const selectors = ['cart-items', '.drawer__heading', '.totals__subtotal-value', '.free-shipping-goal' ];

                        for (const selector of selectors) {                            
                            const targetElement = document.querySelector(selector);
                            const sourceElement = html.querySelector(selector);

                            if (targetElement && sourceElement) {
                                targetElement.replaceWith(sourceElement);
                            }
                        }

                        return
                    }

                    window.location.reload();
                })
                .catch((error) => {
                    console.error('Error:', error);
                });
        } catch (error) {
            console.log(`Erro removeItemMotagem: ${error}`)
        }
    }

}

customElements.define("button-remove-kit", ButtonRemoveKit)
